import { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  getOrganizationUsage,
  getOrganizationAIUsage,
  UsageStats,
  AIGenerationUsageStats,
  AIGeneratorType,
} from "../../api/organization";

interface OrganizationUsageProps {
  organizationId: string;
  isAdmin?: boolean;
}

const GENERATOR_LABELS: Record<AIGeneratorType, string> = {
  TITLE_GENERATION: "Title",
  COLUMN_DESCRIPTION_GENERATION: "Column Description",
  SHORT_DESCRIPTION_GENERATION: "Short Description",
  LONG_DESCRIPTION_GENERATION: "Long Description",
  REGULAR_DESCRIPTION_GENERATION: "Regular Description",
};

export const OrganizationUsage: React.FC<OrganizationUsageProps> = ({
  organizationId,
  isAdmin = false,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [translationStats, setTranslationStats] = useState<UsageStats | null>(
    null
  );
  const [aiStats, setAIStats] = useState<AIGenerationUsageStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch translation usage for all users
      const translationResponse = await getOrganizationUsage(organizationId);
      setTranslationStats(translationResponse?.data?.data || null);

      // Only fetch AI usage for admins
      if (isAdmin) {
        const aiResponse = await getOrganizationAIUsage(organizationId);
        setAIStats(aiResponse?.data?.data || null);
      }
    } catch (error) {
      console.error("Error fetching usage:", error);
    } finally {
      setLoading(false);
    }
  }, [organizationId, isAdmin]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const formatNumber = (n: number) => n.toLocaleString();
  const formatCost = (n: number) => `$${n.toFixed(4)}`;

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  const hasTranslationData =
    translationStats && translationStats.monthly.length > 0;
  const hasAIData = isAdmin && aiStats && aiStats.monthly.length > 0;

  // For non-admins, only show translation data
  const hasAnyData = isAdmin
    ? hasTranslationData || hasAIData
    : hasTranslationData;

  if (!hasAnyData) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={2}>
          {isAdmin ? "Usage Statistics" : "Translation Usage Statistics"}
        </Typography>
        <Typography color="text.secondary">No usage data yet</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" mb={2}>
        {isAdmin ? "Usage Statistics" : "Translation Usage Statistics"}
      </Typography>

      {isAdmin ? (
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 2 }}
        >
          <Tab label="AI Generation" />
          <Tab label="Translation" />
        </Tabs>
      ) : null}

      {/* AI Generation Tab - Admin only */}
      {isAdmin && tabValue === 0 && (
        <>
          {!hasAIData ? (
            <Typography color="text.secondary">
              No AI generation usage data yet
            </Typography>
          ) : (
            <>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                mb={2}
              >
                <Chip
                  label={`Total: ${formatCost(aiStats!.grand_total_cost_usd)}`}
                  color="secondary"
                />
                <Chip
                  label={`${formatNumber(
                    aiStats!.grand_total_generations
                  )} generations`}
                  variant="outlined"
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell>Generator</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell align="right">Cost (USD)</TableCell>
                      <TableCell align="right">Generations</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aiStats!.monthly.flatMap((monthData) =>
                      Object.entries(monthData.by_generator).flatMap(
                        ([generator, genData]) =>
                          genData?.models.map((model) => (
                            <TableRow
                              key={`${monthData.month}-${generator}-${model.name}`}
                            >
                              <TableCell>{monthData.month}</TableCell>
                              <TableCell>
                                {GENERATOR_LABELS[
                                  generator as AIGeneratorType
                                ] || generator}
                              </TableCell>
                              <TableCell>{model.name}</TableCell>
                              <TableCell align="right">
                                {formatCost(model.cost_usd)}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(model.generations)}
                              </TableCell>
                            </TableRow>
                          )) || []
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {/* Translation Tab - shown for all users, but with tabs only for admin */}
      {(isAdmin ? tabValue === 1 : true) && (
        <>
          {!hasTranslationData ? (
            <Typography color="text.secondary">
              No translation usage data yet
            </Typography>
          ) : (
            <>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mb={2}
              >
                <Chip
                  label={`Total: ${formatNumber(
                    translationStats!.grand_total
                  )} chars`}
                  color="secondary"
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Characters</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {translationStats!.monthly.map((month) => (
                      <TableRow key={month.month}>
                        <TableCell>{month.month}</TableCell>
                        <TableCell align="right">
                          {formatNumber(month.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Paper>
  );
};
